import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X, Download, Loader2 } from "lucide-react";
import { assetUrl } from "../../api/client.js";

/**
 * Full-screen, immersive image viewer.
 *
 * Layout strategy:
 *  - The `<img>` element itself is forced to the full viewport
 *    (`h-full w-full`) and uses `object-contain` so the image scales up to
 *    fill the screen at the largest size that preserves aspect ratio. This
 *    is critical: with `max-h-full max-w-full` the element only CAPS at
 *    viewport size — small uploads stay small. With `h-full w-full` we
 *    force the element to viewport size and let object-contain scale the
 *    source to match.
 *  - Chrome (close, counter, prev/next, "Full size" link) overlays on top
 *    of the image rather than reserving space, so the visual takes the
 *    whole screen.
 *  - Backdrop click + ESC close. Image click is ignored so swiping/tapping
 *    on the photo doesn't dismiss the lightbox.
 */
export function Lightbox({ images = [], index, onClose, onChange }) {
  const [loading, setLoading] = useState(true);

  // Reset the loading flag whenever the active image changes so the
  // spinner overlay renders until the new src finishes decoding.
  useEffect(() => {
    setLoading(true);
  }, [index]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft" && index > 0) onChange(index - 1);
      if (e.key === "ArrowRight" && index < images.length - 1) onChange(index + 1);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [index, images.length, onClose, onChange]);

  const src = images[index];
  if (!src) return null;
  const resolved = assetUrl(src);

  return (
    <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true">
      {/* Backdrop — clicking anywhere outside the chrome closes the
          viewer. Solid near-black for the immersive feel. */}
      <div
        className="absolute inset-0 bg-black backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      {/* IMAGE — covers the entire viewport. `h-full w-full object-contain`
          forces the element to the screen size and scales the image up to
          match while preserving aspect ratio. The image takes clicks (so
          tapping the photo doesn't dismiss); the surrounding backdrop
          closes the viewer. */}
      {loading && (
        <Loader2 className="pointer-events-none absolute left-1/2 top-1/2 z-10 h-12 w-12 -translate-x-1/2 -translate-y-1/2 animate-spin text-white/70" />
      )}
      <img
        // Re-mount on src change so the loading overlay re-shows during
        // each decode, and the fade-in restarts cleanly.
        key={resolved}
        src={resolved}
        alt=""
        onLoad={() => setLoading(false)}
        onClick={(e) => e.stopPropagation()}
        className="absolute inset-0 block h-full w-full select-none object-contain transition-opacity duration-200"
        style={{ opacity: loading ? 0 : 1 }}
        draggable={false}
      />

      {/* Top chrome — image counter (left) + Full-size download + Close
          (right). Sits over the image; doesn't reserve viewport space. */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex items-center justify-between bg-gradient-to-b from-black/70 to-transparent px-4 py-4 sm:px-6">
        <span className="pointer-events-auto rounded-full bg-white/10 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-md">
          {images.length > 1 ? `${index + 1} / ${images.length}` : "Image"}
        </span>
        <div className="pointer-events-auto flex items-center gap-2">
          <a
            href={resolved}
            download
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs text-white backdrop-blur-md transition-colors hover:bg-white/20"
            aria-label="Open image at full resolution"
          >
            <Download className="h-3.5 w-3.5" /> Full size
          </a>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-md transition-colors hover:bg-white/20"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Prev / next nav — generously sized, always reachable on mobile. */}
      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange((index - 1 + images.length) % images.length);
            }}
            className="absolute left-3 top-1/2 z-20 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-md transition-colors hover:bg-white/20 sm:left-6 sm:h-14 sm:w-14"
            aria-label="Previous image"
          >
            <ChevronLeft className="h-6 w-6 sm:h-7 sm:w-7" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange((index + 1) % images.length);
            }}
            className="absolute right-3 top-1/2 z-20 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-md transition-colors hover:bg-white/20 sm:right-6 sm:h-14 sm:w-14"
            aria-label="Next image"
          >
            <ChevronRight className="h-6 w-6 sm:h-7 sm:w-7" />
          </button>
        </>
      )}
    </div>
  );
}
